"use client"

import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { ArrowLeft, Loader2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { API_ENDPOINTS } from "@/constants/config"
import toast from "react-hot-toast"

const MAX_IMAGES = 5

const DEFAULT_ITEM = {
  item_name: "",
  category: "",
  sub_category: "",
  images: [],
  vendor_id: "",
  vendor_name: "",
  purchase_date: "",
  purchase_cost: 0,
  quantity_purchased: 0,
  quantity_available: 0,
  minimum_stock_level: 0,
  reorder_level: 0,
  unit_of_measurement: "",
  storage_location: "",
  description: "",
  brand: "",
  size: "",
  custom_attributes: {},
  is_active: true,
}

function Field({ label, required, children, span2 = false }) {
  return (
    <div className={span2 ? "md:col-span-2" : ""}>
      <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  )
}

export default function InventoryItemFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const isEdit = Boolean(id)

  // Reference data
  const [categories, setCategories] = useState([])
  const [vendors, setVendors] = useState([])
  const [units, setUnits] = useState([])
  const [sizes, setSizes] = useState([])
  const [brands, setBrands] = useState([])
  const [colors, setColors] = useState([])
  const [isColorEnabled, setIsColorEnabled] = useState(false)
  const [subCategories, setSubCategories] = useState([])
  const [isSubCategoryEnabled, setIsSubCategoryEnabled] = useState(false)

  const [formData, setFormData] = useState(DEFAULT_ITEM)
  const [pageLoading, setPageLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [brandSuggestions, setBrandSuggestions] = useState([])
  const [newImages, setNewImages] = useState([]) // [{ file, previewUrl }]
  const newImagesRef = useRef(newImages)
  newImagesRef.current = newImages

  useEffect(() => {
    return () => {
      newImagesRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl))
    }
  }, [])

  const token = () => localStorage.getItem("token")

  const get = (url) =>
    fetch(url, { headers: { Authorization: `Bearer ${token()}` } }).then((r) => r.json())

  useEffect(() => {
    const load = async () => {
      try {
        setPageLoading(true)

        const [catRes, vendorRes, unitRes, sizeRes, brandRes, colorCfg] = await Promise.all([
          get(API_ENDPOINTS.inventoryCategories),
          get(API_ENDPOINTS.vendors),
          get(API_ENDPOINTS.inventoryUnits),
          get(API_ENDPOINTS.inventorySizes),
          get(API_ENDPOINTS.inventoryBrands),
          get(API_ENDPOINTS.inventoryFeatureConfig),
        ])

        setCategories(catRes.data || [])
        setVendors(vendorRes.data || [])
        setUnits(unitRes.data || [])
        setSizes(sizeRes.data || [])
        setBrands(brandRes.data || [])

        const colorOn = Boolean(colorCfg?.data?.features?.color)
        setIsColorEnabled(colorOn)

        if (colorOn) {
          const colorRes = await get(API_ENDPOINTS.inventoryColors)
          setColors(colorRes.data || [])
        }

        const subCategoryOn = Boolean(colorCfg?.data?.features?.sub_category)
        setIsSubCategoryEnabled(subCategoryOn)

        const loadSubCategoriesFor = async (categoryName) => {
          const cat = (catRes.data || []).find((c) => c.name === categoryName)
          if (!cat) return
          const subCatRes = await get(`${API_ENDPOINTS.inventorySubCategories}?category=${cat._id}`)
          setSubCategories(subCatRes.data || [])
        }

        // Populate form for edit
        if (isEdit) {
          const stateItem = location.state?.item
          if (stateItem) {
            setFormData({
              ...DEFAULT_ITEM,
              ...stateItem,
              images: stateItem.images || [],
              custom_attributes: stateItem.custom_attributes || {},
              purchase_date: stateItem.purchase_date
                ? new Date(stateItem.purchase_date).toISOString().split("T")[0]
                : "",
              is_active: stateItem.is_active !== false,
            })
            if (subCategoryOn) await loadSubCategoriesFor(stateItem.category)
          } else {
            const itemRes = await get(API_ENDPOINTS.inventoryItem(id))
            const item = itemRes.data
            setFormData({
              ...DEFAULT_ITEM,
              ...item,
              images: item.images || [],
              custom_attributes: item.custom_attributes || {},
              purchase_date: item.purchase_date
                ? new Date(item.purchase_date).toISOString().split("T")[0]
                : "",
              is_active: item.is_active !== false,
            })
            if (subCategoryOn) await loadSubCategoriesFor(item.category)
          }
        }
      } catch (err) {
        toast.error("Failed to load form data")
      } finally {
        setPageLoading(false)
      }
    }
    load()
  }, [id])

  const set = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }))

  const handleCategoryChange = async (categoryName) => {
    setFormData((prev) => ({ ...prev, category: categoryName, sub_category: "" }))
    if (!isSubCategoryEnabled) return
    const cat = categories.find((c) => c.name === categoryName)
    if (!cat) {
      setSubCategories([])
      return
    }
    try {
      const res = await get(`${API_ENDPOINTS.inventorySubCategories}?category=${cat._id}`)
      setSubCategories(res.data || [])
    } catch {
      setSubCategories([])
    }
  }

  const handleBrandInput = (val) => {
    set("brand", val)
    setBrandSuggestions(
      val ? brands.filter((b) => b.name.toLowerCase().startsWith(val.toLowerCase())).slice(0, 8) : []
    )
  }

  const totalImageCount = formData.images.length + newImages.length

  const handleImageFilesChange = (e) => {
    const files = Array.from(e.target.files || [])
    const remainingSlots = MAX_IMAGES - totalImageCount
    if (remainingSlots <= 0) {
      toast.error(`You can upload a maximum of ${MAX_IMAGES} images`)
      e.target.value = ""
      return
    }
    if (files.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more image(s) can be added (max ${MAX_IMAGES})`)
    }
    const additions = files.slice(0, remainingSlots).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }))
    setNewImages((prev) => [...prev, ...additions])
    e.target.value = ""
  }

  const removeExistingImage = (index) => {
    setFormData((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }))
  }

  const removeNewImage = (index) => {
    setNewImages((prev) => {
      const removed = prev[index]
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.item_name || !formData.category) {
      toast.error("Item name and category are required")
      return
    }
    if (!formData.vendor_id && !formData.brand) {
      toast.error("Please select a vendor or enter a brand")
      return
    }

    const payload = { ...formData }
    if (!payload.custom_attributes) payload.custom_attributes = {}
    if (!isColorEnabled) delete payload.custom_attributes.color
    if (!isSubCategoryEnabled) delete payload.sub_category
    if (!payload.vendor_id || payload.vendor_id === "placeholder") {
      delete payload.vendor_id
      delete payload.vendor_name
    }
    if (isEdit) {
      delete payload.quantity_available
      delete payload.quantity_purchased
      delete payload.on_hold_quantity
    }

    try {
      setSubmitting(true)
      const url = isEdit ? API_ENDPOINTS.inventoryItem(id) : API_ENDPOINTS.inventory
      const method = isEdit ? "PUT" : "POST"

      let res
      if (newImages.length > 0) {
        const formDataBody = new FormData()
        formDataBody.append("data", JSON.stringify(payload))
        newImages.forEach(({ file }) => formDataBody.append("images", file))
        res = await fetch(url, {
          method,
          headers: { Authorization: `Bearer ${token()}` },
          body: formDataBody,
        })
      } else {
        res = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token()}`,
          },
          body: JSON.stringify(payload),
        })
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || "Failed to save item")
      }
      toast.success(isEdit ? "Item updated" : "Item added")
      navigate(isEdit ? `/dashboard/inventory/${id}` : "/dashboard/inventory")
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600 mr-2" />
        <span className="text-sm text-gray-500">Loading…</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full flex-shrink-0"
          onClick={() => navigate(isEdit ? `/dashboard/inventory/${id}` : "/dashboard/inventory")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit Item" : "Add New Item"}
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {isEdit ? `Editing: ${formData.item_name}` : "Fill in the details to add a new inventory item"}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Basic Info */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-100">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Item Name" required>
                <Input
                  value={formData.item_name}
                  onChange={(e) => set("item_name", e.target.value)}
                  placeholder="e.g. A4 Paper Ream"
                />
              </Field>

              <Field label="Category" required>
                <Select value={formData.category} onValueChange={handleCategoryChange}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c._id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {isSubCategoryEnabled && (
                <Field label="Sub-category">
                  <Select
                    value={formData.sub_category || "none"}
                    onValueChange={(v) => set("sub_category", v === "none" ? "" : v)}
                    disabled={!formData.category}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={subCategories.length ? "Select sub-category" : "No sub-categories configured"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {subCategories.map((sc) => (
                        <SelectItem key={sc._id} value={sc.name}>{sc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <Field label="Description" span2>
                <Input
                  value={formData.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Optional description"
                />
              </Field>

              <Field label="Active Status">
                <Select
                  value={formData.is_active ? "active" : "inactive"}
                  onValueChange={(v) => set("is_active", v === "active")}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label={`Item Images (${totalImageCount}/${MAX_IMAGES})`} span2>
                <div className="space-y-3">
                  {totalImageCount > 0 && (
                    <div className="flex flex-wrap gap-3">
                      {formData.images.map((url, index) => (
                        <div key={`existing-${url}`} className="relative h-20 w-20">
                          <img
                            src={url}
                            alt={`Item ${index + 1}`}
                            className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(index)}
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-red-600"
                            aria-label="Remove image"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {newImages.map((img, index) => (
                        <div key={img.previewUrl} className="relative h-20 w-20">
                          <img
                            src={img.previewUrl}
                            alt={`New item ${index + 1}`}
                            className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeNewImage(index)}
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-red-600"
                            aria-label="Remove image"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {totalImageCount < MAX_IMAGES && (
                    <Input type="file" accept="image/*" multiple onChange={handleImageFilesChange} className="max-w-xs" />
                  )}
                </div>
              </Field>
            </div>
          </section>

          {/* Stock & Pricing */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-100">Stock & Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isEdit && (
                <Field label="Initial Quantity">
                  <Input
                    type="number" step="any"
                    value={formData.quantity_purchased}
                    onChange={(e) => {
                      const qty = parseFloat(e.target.value) || 0
                      setFormData((prev) => ({ ...prev, quantity_purchased: qty, quantity_available: qty }))
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                  />
                </Field>
              )}

              <Field label="Unit of Measurement">
                <Select value={formData.unit_of_measurement} onValueChange={(v) => set("unit_of_measurement", v)}>
                  <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => (
                      <SelectItem key={u._id} value={u.name}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Purchase Cost (₹)">
                <Input
                  type="number"
                  value={formData.purchase_cost}
                  onChange={(e) => set("purchase_cost", parseFloat(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                />
              </Field>

              <Field label="Purchase Date">
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => set("purchase_date", e.target.value)}
                />
              </Field>

              <Field label="Minimum Stock Level">
                <Input
                  type="number" step="any"
                  value={formData.minimum_stock_level}
                  onChange={(e) => set("minimum_stock_level", parseFloat(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                />
              </Field>

              <Field label="Reorder Level">
                <Input
                  type="number" step="any"
                  value={formData.reorder_level}
                  onChange={(e) => set("reorder_level", parseFloat(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                />
              </Field>

              <Field label="Storage Location">
                <Input
                  value={formData.storage_location}
                  onChange={(e) => set("storage_location", e.target.value)}
                  placeholder="e.g. Shelf A3, Storeroom 2"
                />
              </Field>
            </div>
          </section>

          {/* Vendor & Attributes */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-100">Vendor & Attributes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Vendor">
                <Select
                  value={formData.vendor_id || "placeholder"}
                  onValueChange={(v) => {
                    const picked = vendors.find((vn) => vn._id === v)
                    setFormData((prev) => ({
                      ...prev,
                      vendor_id: v === "placeholder" ? "" : v,
                      vendor_name: picked ? picked.name : "",
                    }))
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder">None</SelectItem>
                    {vendors.map((v) => (
                      <SelectItem key={v._id} value={v._id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Brand">
                <div className="relative">
                  <Input
                    value={formData.brand}
                    onChange={(e) => handleBrandInput(e.target.value)}
                    placeholder="Type to search brands"
                    autoComplete="off"
                  />
                  {brandSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {brandSuggestions.map((b) => (
                        <button
                          key={b._id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                          onClick={() => { set("brand", b.name); setBrandSuggestions([]) }}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Size">
                <Select value={formData.size || "none"} onValueChange={(v) => set("size", v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {sizes.map((s) => (
                      <SelectItem key={s._id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {isColorEnabled && (
                <Field label="Color">
                  <Select
                    value={formData.custom_attributes?.color || "none"}
                    onValueChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        custom_attributes: {
                          ...(prev.custom_attributes || {}),
                          color: v === "none" ? "" : v,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={colors.length ? "Select color" : "No colors configured"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {colors.map((c) => (
                        <SelectItem key={c._id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          </section>

          {/* Actions */}
          <div className="flex items-center gap-3 pb-8">
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? "Update Item" : "Add Item"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isEdit ? `/dashboard/inventory/${id}` : "/dashboard/inventory")}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
